import { useFilters, useFiltersDispatch } from '../TrackerFilter/Context/FiltersContext';
import { useLocation, useParams } from 'react-router-dom';
import { getCurrMonth } from '../../shared/functions/dateProcessor';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useServiceLogHandler } from '../../shared/eventHandler/ServiceLogEventHandler';
import {
  filterStorageTypeMap,
  TABLE_REFETCH_INTERVAL_MILISEC,
  TrackerDataType,
  visibleEngagementLogCols,
} from '../../shared/constants/constants';
import isFilterEmpty from '../../shared/functions/isFilterEmpty';
import { Box } from '@mui/material';
import TrackerTable from '../TrackerTable/TrackerTable';
import Spinner from 'react-bootstrap/Spinner';
import { DropdownTable, FlatEngagement, UserMeta } from '../../shared/types';
import makeStyles from '@mui/styles/makeStyles';
import { useEngagementLogHandler } from '../../shared/eventHandler/EngagementLogEventHandler';
import dayjs from '../../utils/dayjs';
import MonthSelector from '../TrackerTable/MonthSelector';
import TrackerTableFuzzySearch from '../TrackerTable/TrackerTableFuzzySearch';
import TrackerTableFilter from '../TrackerTable/TrackerTableFilter';
import { TrackerTableActionButtons } from '../TrackerTable/TrackerTableActionButtons';
import { useRecentCreations } from '../../shared/hooks/useRecentCreations';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getFilteredEngagements, getMonthlyEngagements } from '../../shared/requests/engagementsRequest';
import { getTrackerFilterQueryFromFilters } from '../TrackerFilter/trackerFilterHelpers';

interface EngagementLogPageProps {
  dropdownTable: DropdownTable;
  user: UserMeta;
}

const useStyles = makeStyles({
  tableContainer: {
    width: 'calc(100% - 60px)',
    margin: '0 auto',
    marginBottom: 30,
  },
  filterContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: '1rem',
    marginBottom: '1rem',
    height: '32px',
  },
  filterWrapper: {
    display: 'flex',
    gap: '1rem',
    alignItems: 'center',
  },
});

export const EngagementLogPage = (props: Readonly<EngagementLogPageProps>) => {
  const { dropdownTable, user } = props;
  const classes = useStyles();
  const filtersDispatch = useFiltersDispatch();
  const { state } = useLocation();
  const recentCreations = useRecentCreations();
  const { markCreated } = recentCreations;
  const previousMonthlyIds = useRef<Set<string> | null>(null);

  let month;
  if (sessionStorage.getItem('currMonth')) {
    month = sessionStorage.getItem('currMonth') as string;
  } else {
    month = getCurrMonth();
  }

  const [data, setData] = useState<FlatEngagement[]>([]);
  const [currMonth, setMonth] = useState(month);
  const [isBulkEdit, setIsBulkEdit] = useState(false);
  const [selectedBulkEditRowIds, setSelectedBulkEditRowIds] = useState<string[]>([]);

  const existingFilters = useFilters();

  const queryClient = useQueryClient();
  const onSuccessCallback = async () => {
    await queryClient.invalidateQueries({ queryKey: ['filteredEngagements'] });
    await queryClient.invalidateQueries({ queryKey: ['monthlyEngagements'] });
    if (state?.queryString && state?.category) {
      await queryClient.invalidateQueries({
        queryKey: ['fuzzySearch', TrackerDataType.ENGAGEMENT, state.queryString, state.category],
      });
    }
  };
  const serviceLogEventHandler = useServiceLogHandler({
    recentCreations,
    onSuccessCallback,
  });
  const engagementLogEventHandler = useEngagementLogHandler({
    recentCreations,
    onSuccessCallback,
  });

  const engagementParams = useParams();

  const { data: monthlyEngagements, refetch: refetchMontlyEngagements } = useQuery({
    queryKey: ['monthlyEngagements', currMonth],
    queryFn: () => getMonthlyEngagements(currMonth, dayjs.tz.guess()),
    enabled: !state && isFilterEmpty(existingFilters),
    refetchInterval: TABLE_REFETCH_INTERVAL_MILISEC,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });

  const filtersQueryString = useMemo(
    () => getTrackerFilterQueryFromFilters(existingFilters, TrackerDataType.ENGAGEMENT),
    [existingFilters]
  );

  const { data: filteredEngagements } = useQuery({
    queryKey: ['filteredEngagements', filtersQueryString],
    queryFn: async () => {
      const data = await getFilteredEngagements(filtersQueryString);
      return data || [];
    },
    enabled: !!filtersQueryString,
  });

  const shouldDisableMonthSwitches = () => {
    const hasFilters = Object.keys(existingFilters).length > 0;
    const isQuickSearching = state?.queryString && state?.category;
    return hasFilters || isQuickSearching;
  };

  const handleBulkEditSelectAll = (isChecked: boolean, pageRowIds: string[]) => {
    if (isChecked) {
      setSelectedBulkEditRowIds(pageRowIds);
    } else {
      setSelectedBulkEditRowIds([]);
    }
  };

  const handleBulkEditSelectRow = (isChecked: boolean, rowId: string) => {
    setSelectedBulkEditRowIds((prevSelectedIds) => {
      if (isChecked) {
        return [...prevSelectedIds, rowId];
      } else {
        return prevSelectedIds.filter((id) => rowId !== id);
      }
    });
  };

  // Filter engagement based on url
  useEffect(() => {
    if (engagementParams.engagementId) {
      // @ts-ignore
      filtersDispatch({
        type: 'setFilters',
        payload: {
          id: engagementParams.engagementId,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [engagementParams]);

  useEffect(() => {
    if (!monthlyEngagements && !filteredEngagements) {
      return;
    }
    const storedFilter = user.nonPIUserSettings?.userStoredFilters?.[filterStorageTypeMap[TrackerDataType.ENGAGEMENT]];
    if (!isFilterEmpty(existingFilters) && filteredEngagements) {
      setData(filteredEngagements);
    } else if (isFilterEmpty(existingFilters) && !state?.queryString && !storedFilter && monthlyEngagements) {
      setData(monthlyEngagements);
    }
  }, [
    monthlyEngagements,
    filteredEngagements,
    existingFilters,
    state,
    user.nonPIUserSettings?.userStoredFilters,
    filtersQueryString,
  ]);

  useEffect(() => {
    previousMonthlyIds.current = null;
  }, [currMonth]);

  useEffect(() => {
    if (!Array.isArray(monthlyEngagements)) {
      return;
    }
    const storedFilter = user.nonPIUserSettings?.userStoredFilters?.[filterStorageTypeMap[TrackerDataType.ENGAGEMENT]];
    const shouldTrack = isFilterEmpty(existingFilters) && !state?.queryString && !storedFilter;
    const nextIds = new Set(monthlyEngagements.map((row) => row.id));

    if (shouldTrack && previousMonthlyIds.current) {
      const prevIds = previousMonthlyIds.current;
      monthlyEngagements.forEach((row) => {
        if (!prevIds.has(row.id)) {
          markCreated(row.id);
        }
      });
    }

    previousMonthlyIds.current = nextIds;
  }, [monthlyEngagements, existingFilters, state, user.nonPIUserSettings?.userStoredFilters, markCreated]);

  useEffect(() => {
    if (typeof currMonth === 'string') {
      sessionStorage.setItem('currMonth', currMonth);

      if (!state) {
        if (isFilterEmpty(existingFilters)) {
          refetchMontlyEngagements();
        }
      }
    }
  }, [currMonth, existingFilters, refetchMontlyEngagements, state]);

  return (
    <Box className={classes.tableContainer}>
      <Box>
        <MonthSelector disabled={shouldDisableMonthSwitches()} currMonth={currMonth} onMonthChange={setMonth} />
      </Box>
      <Box className={classes.filterContainer}>
        <Box className={classes.filterWrapper}>
          <TrackerTableFuzzySearch />
          <TrackerTableFilter
            setDataCallback={setData}
            eventHandler={engagementLogEventHandler}
            dropdownTable={dropdownTable}
          />
        </Box>
        <Box className={classes.filterWrapper}>
          <TrackerTableActionButtons
            eventHandler={engagementLogEventHandler}
            dropdownTable={dropdownTable}
            currMonth={currMonth}
            rows={data}
            isBulkEdit={isBulkEdit}
            selectedBulkEditRowIds={selectedBulkEditRowIds}
            selectedBulkEditRowsCount={selectedBulkEditRowIds.length}
            onSwitchToBulkEditView={() => {
              setIsBulkEdit(true);
            }}
            onSwitchToMonthlyView={() => {
              setIsBulkEdit(false);
              setSelectedBulkEditRowIds([]);
            }}
            hasAutoAdd={true}
          />
        </Box>
      </Box>
      {data && dropdownTable ? (
        <>
          <TrackerTable
            rows={data}
            dropdownTable={dropdownTable}
            visibleCols={visibleEngagementLogCols}
            eventHandler={engagementLogEventHandler}
            linkedEventHandler={serviceLogEventHandler}
            isBulkEdit={isBulkEdit}
            selectedBulkEditRowIds={selectedBulkEditRowIds}
            onBulkEditSelectAll={handleBulkEditSelectAll}
            onBulkEditSelectRow={handleBulkEditSelectRow}
          />
        </>
      ) : (
        <div className="spinner-container">
          <Spinner animation="border" />
        </div>
      )}
    </Box>
  );
};
